import { z } from 'zod';

export const itemTypeSchema = z.enum(['block', 'section', 'full-page']);
export const tailwindVersionSchema = z.enum(['v4', 'v3', 'none']);
export const itemFilterTermSchema = z.object({
  slug: z.string(),
  name: z.string(),
});

export const controlledTagTermSchema = z.object({
  slug: z.string(),
  name: z.string(),
  group: z.string(),
  description: z.string().default(''),
  sortOrder: z.number().int().default(0),
  active: z.boolean().default(true),
});

export const filterTagTermSchema = controlledTagTermSchema.extend({
  count: z.number().int().nonnegative().default(0),
});

export const filterTagGroupSchema = z.object({
  slug: z.string(),
  name: z.string(),
  description: z.string().default(''),
  sortOrder: z.number().int().default(0),
  terms: z.array(filterTagTermSchema).default([]),
});

export const itemFiltersSchema = z.object({
  types: z.array(itemFilterTermSchema).default([]),
  ui: z.array(itemFilterTermSchema).default([]),
  tags: z.array(filterTagTermSchema).default([]),
  tagGroups: z.array(filterTagGroupSchema).default([]),
});

export const itemBaseSchema = z.object({
  id: z.number().int().positive(),
  slug: z.string(),
  title: z.string(),
  excerpt: z.string().default(''),
  previewImageUrl: z.string().nullable().optional(),
  previewImageWidth: z.number().int().positive().nullable().optional(),
  previewImageHeight: z.number().int().positive().nullable().optional(),
  savedCount: z.number().int().nonnegative().default(0),
  type: itemTypeSchema,
  primaryRole: controlledTagTermSchema.nullable().default(null),
  tags: z.array(controlledTagTermSchema).default([]),
  uiTags: z.array(z.string()).default([]),
  searchScore: z.number().optional(),
  tailwindVersion: tailwindVersionSchema.default('v4'),
  isFeatured: z.boolean().default(false),
});

export const previewViewportsSchema = z.object({
  desktop: z.boolean().default(true),
  tablet: z.boolean().default(true),
  mobile: z.boolean().default(true),
});

export const itemListItemSchema = itemBaseSchema;

export const itemDetailSchema = itemBaseSchema.extend({
  description: z.string().default(''),
  code: z.string().default(''),
  adminEditUrl: z.string().nullable().optional(),
  previewViewports: previewViewportsSchema.default({
    desktop: true,
    tablet: true,
    mobile: true,
  }),
});

export const paginatedItemListSchema = z.object({
  data: z.array(itemListItemSchema),
  total: z.number().int().nonnegative().default(0),
  totalPages: z.number().int().nonnegative().default(0),
});

export const aiSearchModeSchema = z.enum(['hybrid']);

export const aiSearchPlanSchema = z.object({
  contractVersion: z.string().default(''),
  query: z.string().default(''),
  requiredTags: z.array(z.string()).default([]),
  preferredTags: z.array(z.string()).default([]),
  excludedTags: z.array(z.string()).default([]),
  relatedRoles: z.array(z.string()).default([]),
  preferredTypes: z.array(itemTypeSchema).default([]),
  notes: z.array(z.string()).default([]),
});

export const aiSearchMetaSchema = z.object({
  plannerProviderRequested: z.string().default(''),
  embeddingProviderRequested: z.string().default(''),
  modelRequested: z.string().default(''),
  dimensionsRequested: z.number().int().default(0),
  candidateLimit: z.number().int().nonnegative().optional(),
  candidateCount: z.number().int().nonnegative().default(0),
  eligibleCount: z.number().int().nonnegative().default(0),
  typeConstraintMode: z.string().default('not_requested'),
  queryEmbeddingModels: z.array(z.string()).default([]),
}).passthrough();

export const aiSearchResponseSchema = z.object({
  query: z.string(),
  mode: aiSearchModeSchema,
  plan: aiSearchPlanSchema,
  meta: aiSearchMetaSchema.default({
    plannerProviderRequested: '',
    embeddingProviderRequested: '',
    modelRequested: '',
    dimensionsRequested: 0,
    candidateCount: 0,
    eligibleCount: 0,
    typeConstraintMode: 'not_requested',
    queryEmbeddingModels: [],
  }),
  notes: z.array(z.string()).default([]),
  items: z.array(itemListItemSchema).default([]),
});

export type ItemType = z.infer<typeof itemTypeSchema>;
export type TailwindVersion = z.infer<typeof tailwindVersionSchema>;
export type ItemFilterTerm = z.infer<typeof itemFilterTermSchema>;
export type ControlledTagTerm = z.infer<typeof controlledTagTermSchema>;
export type FilterTagTerm = z.infer<typeof filterTagTermSchema>;
export type FilterTagGroup = z.infer<typeof filterTagGroupSchema>;
export type ItemFilters = z.infer<typeof itemFiltersSchema>;
export type ItemListItem = z.infer<typeof itemListItemSchema>;
export type ItemDetail = z.infer<typeof itemDetailSchema>;
export type PaginatedItemList = z.infer<typeof paginatedItemListSchema>;
export type PreviewViewports = z.infer<typeof previewViewportsSchema>;
export type AiSearchMode = z.infer<typeof aiSearchModeSchema>;
export type AiSearchPlan = z.infer<typeof aiSearchPlanSchema>;
export type AiSearchMeta = z.infer<typeof aiSearchMetaSchema>;
export type AiSearchResponse = z.infer<typeof aiSearchResponseSchema>;
